/**
 * @deprecated Superseded by the inline WorkspaceSwitcherMenu in the sidebar.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { PermissionRoles } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { GearbeltLoader } from '@gearbelt/ui/icons';
import { GearbeltPlanType, IGetSuperuserWorkspaceSwitcherResponseDto, IGetWorkspaceSwitcherResponseDto, Products } from '@site-mate/sitemate-global-shared';
import { SitemateRegions } from '@site-mate/sitemate-infra-shared/models';
import { WorkspaceUrlService } from '@site-mate/sitemate-infra-shared/services';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';

import manholeShrugVector from '@/assets/vectors/manhole-shrug.svg';
import DashpivotIcon from '@/assets/workspace-switcher/dashpivot-icon.svg';
import FlowsiteIcon from '@/assets/workspace-switcher/flowsite-icon.svg';
import GearbeltIcon from '@/assets/workspace-switcher/gearbelt-icon.svg';
import {
  getSitemateStartAccountQueryOptions,
  getWorkspaceSwitcherSearchQueryOptions,
  getWorkspaceSwitcherWorkspacesQueryOptions,
} from '@/queries/workspace-switcher';
import { useUserStore } from '@/stores/user.store';
import { useWorkspaceSwitcherStore } from '@/stores/workspace-switcher-store';
import { toInfraProduct } from '@/utils/product';

import SearchBar from '../common/search-bar';
import WarningIcon from '../icons/warning';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import CreateWorkspace from './create-workspace';

const WorkspaceSwitcherWarningBanner = ({ planType }: { planType: GearbeltPlanType }) => {
  const { setIsUpgradePlanOpen } = useWorkspaceSwitcherStore();

  const ineligiblePlanType = useMemo(() => {
    if ([GearbeltPlanType.FreeTrial, GearbeltPlanType.Standard].includes(planType)) {
      return planType;
    }

    return null;
  }, [planType]);

  if (!ineligiblePlanType) {
    return null;
  }

  return (
    <div className="bg-default-light-orange text-default-dark-orange flex flex-row items-center gap-x-1 rounded-lg p-2">
      <div className="w-[20px]">
        <WarningIcon />
      </div>

      {ineligiblePlanType === GearbeltPlanType.FreeTrial && (
        <div className="text-sm">
          Creating additional Gearbelt workspaces is not included in our Free Trial:{' '}
          <span className="text-default-blue cursor-pointer underline" onClick={() => setIsUpgradePlanOpen(true)}>
            Upgrade to a paid plan now to unlock this feature
          </span>
        </div>
      )}

      {ineligiblePlanType === GearbeltPlanType.Standard && (
        <div className="text-sm">
          Creating additional Gearbelt workspaces is not included on the Standard Plan:{' '}
          <span className="text-default-blue cursor-pointer underline" onClick={() => setIsUpgradePlanOpen(true)}>
            Upgrade your plan to unlock this feature
          </span>
        </div>
      )}
    </div>
  );
};

const WorkspaceSwitcherSearchBar = ({ search, setSearch }: { search: string; setSearch: (search: string) => void }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return <SearchBar ref={inputRef} search={search} setSearch={setSearch} />;
};

const WorkspaceSwitcherProductIcon = ({ product }: { product: Products }) => {
  const IconMap: Record<string, string> = {
    [Products.Gearbelt]: GearbeltIcon,
    [Products.Dashpivot]: DashpivotIcon,
    [Products.Flowsite]: FlowsiteIcon,
  };

  return IconMap[product] && <img src={IconMap[product]} alt={product} className="h-4 w-4" />;
};

const WorkspaceSwitcherList = ({ workspaces }: { workspaces: IGetSuperuserWorkspaceSwitcherResponseDto[] | IGetWorkspaceSwitcherResponseDto[] }) => {
  const workspaceUrlService = useMemo(() => new WorkspaceUrlService(), []);

  function generateWorkspaceUrl(workspace: IGetSuperuserWorkspaceSwitcherResponseDto | IGetWorkspaceSwitcherResponseDto) {
    let url = workspaceUrlService.getWorkspaceUrl(window.location, {
      id: workspace.workspaceId,
      product: toInfraProduct(workspace.product),
      legacyId: workspace.legacyId ?? undefined,
      region: (workspace.region as SitemateRegions) ?? undefined,
      subdomain: workspace.subdomain ?? undefined,
    });

    if (workspace.product === Products.Gearbelt) {
      url = `${url}/dashboard`;
    }

    return url;
  }

  return (
    <div className="flex w-full flex-col">
      <ScrollArea className="max-h-[400px]">
        {workspaces.map((workspace) => (
          <a
            key={workspace.workspaceId}
            href={generateWorkspaceUrl(workspace)}
            className="border-grey-3 hover:bg-grey-1 flex w-full cursor-pointer flex-row items-center justify-between gap-x-1 border-b px-2 py-1 last:border-0">
            <div className="flex flex-grow flex-row items-center gap-1">
              <div className="flex h-[28px] w-[56px] items-center justify-start">
                <img src={workspace.logo} className="h-full object-contain" alt={workspace.name} />
              </div>

              <div className="flex max-w-[300px] flex-col overflow-hidden">
                <span className="text-default-text truncate text-sm font-medium">{workspace.name}</span>
                <span className="text-secondary-text truncate text-sm">{workspace.accountName}</span>
              </div>
            </div>

            <WorkspaceSwitcherProductIcon product={workspace.product} />
          </a>
        ))}
      </ScrollArea>
    </div>
  );
};

const useSearchWorkspaces = (search: string, isSuperUser: boolean) => {
  const {
    data: allWorkspaces,
    isLoading: isLoadingAllWorkspaces,
    error: errorAllWorkspaces,
  } = useQuery({
    ...getWorkspaceSwitcherWorkspacesQueryOptions(),
    enabled: !isSuperUser,
  });

  const {
    data: searchResults,
    isLoading: isLoadingSearchResults,
    error: errorSearchResults,
  } = useQuery({
    ...getWorkspaceSwitcherSearchQueryOptions({ search }),
    // We only search Gearbelt workspaces if we're a superuser for performance reasons.
    enabled: isSuperUser && search.length > 0,
  });

  if (isSuperUser) {
    return { data: searchResults, isLoading: isLoadingSearchResults, error: errorSearchResults };
  }

  const filteredWorkspaces = allWorkspaces?.filter((workspace) => workspace.name.toLowerCase().includes(search.toLowerCase()));

  return { data: filteredWorkspaces, isLoading: isLoadingAllWorkspaces, error: errorAllWorkspaces };
};

const WorkspaceSwitcher = () => {
  const { user } = useUserStore();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const isSuperUser = user?.roles.includes(PermissionRoles.SitemateSuperUser) ?? false;
  const { data: workspaces, isLoading, error } = useSearchWorkspaces(debouncedSearch, isSuperUser);

  const renderContent = () => {
    if (!debouncedSearch && isSuperUser) {
      return (
        <div className="text-default-text flex min-h-[80px] items-center justify-center text-sm">
          <span className="text-secondary-text">Start searching for a workspace</span>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="flex min-h-[80px] items-center justify-center gap-1">
          <GearbeltLoader size={27} />
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-default-text flex min-h-[80px] items-center justify-center text-sm">
          <span>Oops! Something went wrong.</span>
        </div>
      );
    }

    if (!workspaces?.length) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 p-[20px]">
          <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
          <span className="text-secondary-text mt-4 text-sm">Oops! No results</span>
        </div>
      );
    }

    return <WorkspaceSwitcherList workspaces={workspaces} />;
  };

  return (
    <div className="flex w-full flex-col">
      <div className="border-grey-3 bg-grey-2 w-[440px] rounded-t border border-b-1 p-1">
        <WorkspaceSwitcherSearchBar search={search} setSearch={setSearch} />
      </div>
      <div className="border-grey-3 flex flex-col items-center justify-center rounded-b border">{renderContent()}</div>
    </div>
  );
};

const WorkspaceSwitcherModal = () => {
  const { isOpen, setIsOpen } = useWorkspaceSwitcherStore();
  const { user, org } = useUserStore();

  const [modalMode, setModalMode] = useState<'create' | 'switch'>('switch');
  useEffect(() => {
    if (isOpen) {
      setModalMode('switch');
    }
  }, [isOpen]);

  const { data: account } = useQuery(getSitemateStartAccountQueryOptions({ accountId: org?.accountId }));

  const planType = useMemo(() => account?.billing?.gearbelt?.planType, [account?.billing?.gearbelt?.planType]);

  const canCreateWorkspace = useMemo(() => {
    const isPlanQualified = planType && [GearbeltPlanType.Legacy, GearbeltPlanType.Pro, GearbeltPlanType.Premium, GearbeltPlanType.Platinum].includes(planType);
    const isUserEligible = user?.roles.includes(PermissionRoles.GearbeltAdmin);

    return user?.roles.includes(PermissionRoles.SitemateSuperUser) || (isPlanQualified && isUserEligible);
  }, [planType, user]);

  if (!org) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="top-0 mt-25 w-[500px] border-white">
        <DialogHeader>
          <DialogTitle>{modalMode === 'switch' ? 'Switch' : 'Create'} Workspace</DialogTitle>
        </DialogHeader>

        {modalMode === 'switch' && (
          <div className="flex flex-col gap-y-4">
            <WorkspaceSwitcher />

            <Button onClick={() => setModalMode('create')} variant="success" className="self-start p-2 font-medium" disabled={!canCreateWorkspace}>
              Create Workspace
            </Button>

            <span className="text-secondary-text text-sm">Only users with Admin permissions can create additional workspaces.</span>

            {planType && <WorkspaceSwitcherWarningBanner planType={planType} />}
          </div>
        )}

        {modalMode === 'create' && <CreateWorkspace account={account} />}
      </DialogContent>
    </Dialog>
  );
};

export default WorkspaceSwitcherModal;
